import { shouldRefreshToken } from '@/libs/auth';

export interface MiddlewareRequestContext {
  getUrl: () => string;
  setHeaderParam: (key: string, value: string) => void;
}

interface MiddlewareResponseContext {
  httpStatusCode: number;
}

interface SdkMiddleware<
  RequestContext extends MiddlewareRequestContext,
  ResponseContext extends MiddlewareResponseContext,
> {
  pre: (context: RequestContext) => Promise<RequestContext>;
  post: (context: ResponseContext) => Promise<ResponseContext>;
}

interface SharedRefreshTokenParams {
  authToken: string;
  baseUrl: string;
  updateAuthToken: (token: string) => void;
  useRefreshToken: boolean;
  refreshTokenIfNeeded: (
    authToken: string,
    baseUrl: string,
    updateAuthToken: (token: string) => void
  ) => Promise<string>;
}

export const createSharedRefreshToken = ({
  authToken,
  baseUrl,
  updateAuthToken,
  useRefreshToken,
  refreshTokenIfNeeded,
}: SharedRefreshTokenParams) => {
  // The live token for this middleware instance. Updated in place when a
  // refresh resolves so subsequent requests evaluate expiry against the fresh
  // token rather than the one captured when the closure was created — otherwise
  // every request after the first keeps re-refreshing until React re-renders.
  let currentToken = authToken;
  // Non-null only while a refresh is in flight, so concurrent requests share the
  // same refresh instead of each firing their own.
  let refreshPromise: Promise<string> | null = null;

  return () => {
    if (!useRefreshToken) {
      return Promise.resolve(currentToken);
    }

    if (refreshPromise) {
      return refreshPromise;
    }

    if (!shouldRefreshToken(currentToken)) {
      return Promise.resolve(currentToken);
    }

    refreshPromise = refreshTokenIfNeeded(
      currentToken,
      baseUrl,
      updateAuthToken
    ).then(
      (token) => {
        currentToken = token;
        refreshPromise = null;
        return token;
      },
      () => {
        refreshPromise = null;
        return currentToken;
      }
    );
    return refreshPromise;
  };
};

export const createAuthMiddleware = <
  RequestContext extends MiddlewareRequestContext,
  ResponseContext extends MiddlewareResponseContext,
>(
  refreshToken: () => Promise<string>,
  serverFlags: string | null,
  useRefreshToken: boolean
): SdkMiddleware<RequestContext, ResponseContext> => ({
  pre: async (context) => {
    if (serverFlags) {
      context.setHeaderParam('X-Server-Flags', serverFlags);
    }

    if (!context.getUrl().endsWith('/refresh')) {
      const newAuthToken = await refreshToken();
      context.setHeaderParam('Authorization', `Bearer ${newAuthToken}`);
    }

    return context;
  },
  post: async (context) => {
    if (useRefreshToken && context.httpStatusCode === 401) {
      location.href = '/logout';
    }
    return context;
  },
});
